import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { API_BASE_URL } from '../../utils/apiClient';
import './Ruffle.css';

/**
 * Ruffle Emulator Component
 * 
 * This component handles Flash (SWF) content in two ways:
 * 1. Single SWF file - When a standalone SWF file is uploaded as a ROM, it's loaded directly
 * 2. ZIP archive - When a ZIP file is uploaded as a ROM, it is extracted during upload to a folder based on the game's ID,
 *    and the main SWF file from that folder is loaded
 * 
 * Launch options can be specified in the game's launchoptions column using the format:
 * --mainfile=game.swf
 * 
 * Available launch options:
 * --mainfile=<filename>    : Specifies which SWF file to load from the extracted folder (e.g., --mainfile=game.swf)
 */
const Ruffle = ({ game, onPlayerRef }) => {
  const ruffleRef = useRef(null);
  const rufflePlayerRef = useRef(null);
  const isLoadingRef = useRef(false);
  
  // Function to force focus on the player
  const forceFocus = () => {
    if (rufflePlayerRef.current && typeof rufflePlayerRef.current.focus === 'function') {
      rufflePlayerRef.current.focus();
    }
  };
  
  // Parse launch options from game object
  const parseLaunchOptions = (launchOptions) => {
    if (!launchOptions) return { mainFile: null };
    
    const options = {};
    
    // Parse main file setting
    const mainFileMatch = launchOptions.match(/--mainfile=([^\s]+)/);
    if (mainFileMatch && mainFileMatch[1]) {
      options.mainFile = mainFileMatch[1];
      console.log(`Ruffle: Using main file: ${options.mainFile}`);
    }
    
    return options;
  };
  
  useEffect(() => {
    // Capture the current ruffleRef value for cleanup
    const currentRuffleRef = ruffleRef.current;
    
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      return;
    }
    
    // Load Ruffle
    const loadRuffle = async () => {
      isLoadingRef.current = true;
      try {
        // Check if Ruffle script is already loaded
        const existingScript = document.querySelector('script[src="/ruffle/ruffle.js"]');
        
        if (!existingScript && !window.RufflePlayer) {
          // Create script element only if it doesn't exist
          const script = document.createElement('script');
          script.src = '/ruffle/ruffle.js';
          script.async = true;

          // Create a promise to wait for script load
          const scriptLoaded = new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
          });

          // Add script to document
          document.head.appendChild(script);

          // Wait for script to load
          await scriptLoaded;
        } else if (existingScript && !window.RufflePlayer) {
          // Wait for existing script to load
          await new Promise((resolve) => {
            const checkRuffle = () => {
              if (window.RufflePlayer) {
                resolve();
              } else {
                setTimeout(checkRuffle, 100);
              }
            };
            checkRuffle();
          });
        }

        // Initialize Ruffle
        if (window.RufflePlayer) {
          const ruffle = window.RufflePlayer.newest();
          const player = ruffle.createPlayer();
          const container = ruffleRef.current;
          
          // Clear container
          if (container) {
            container.innerHTML = '';
            
            // Set explicit dimensions on the player element before appending
            player.style.width = '100%';
            player.style.height = '100%';
            container.appendChild(player);
            
            // Store the player reference and pass to parent
            rufflePlayerRef.current = player;
            if (onPlayerRef) onPlayerRef(player);
            
            // Determine the SWF URL to load
            let swfUrl;
            let baseUrl;
            const gameId = game.id;
            
            // Parse launch options
            const launchOptions = parseLaunchOptions(game.launchoptions);
            
            // Check if the ROM path is a direct SWF file or if we need to load from extracted folder
            if (game.rom.toLowerCase().endsWith('.swf')) {
              // Direct SWF file
              swfUrl = `${API_BASE_URL}${game.rom}`;
              // Base URL is the directory containing the SWF
              baseUrl = swfUrl.substring(0, swfUrl.lastIndexOf('/') + 1);
              console.log(`Ruffle: Loading direct SWF file: ${swfUrl}`);
            } else if (game.rom.toLowerCase().endsWith('.zip') && launchOptions.mainFile) {
              // Multi-file Flash game - load from extracted folder
              swfUrl = `${API_BASE_URL}/roms/flash/${gameId}/${launchOptions.mainFile}`;
              // Base URL is the directory containing all the extracted files
              baseUrl = `${API_BASE_URL}/roms/flash/${gameId}/`;
              console.log(`Ruffle: Loading multi-file Flash game: ${swfUrl}`);
              console.log(`Ruffle: Base URL for relative paths: ${baseUrl}`);
            } else {
              // Fallback to direct ROM path
              swfUrl = `${API_BASE_URL}${game.rom}`;
              baseUrl = swfUrl.substring(0, swfUrl.lastIndexOf('/') + 1);
              console.log(`Ruffle: Loading fallback ROM path: ${swfUrl}`);
            }
            
            // Set config before loading the SWF
            player.config = {
              // Ruffle must enter its autoplay path so it installs the
              // click-to-resume handler required by Firefox and mobile
              // browsers when the Web Audio context starts suspended.
              autoplay: "on",
              logLevel: "debug",
              unmuteOverlay: "visible",
              letterbox: "on",
              contextMenu:"on",
              backgroundColor: "#000000",
              scale: "showAll",
              quality: "high",
              wmode: "direct",
              menu: true,
              fontSources: ["/ruffle/SourceHanSansSC-Regular.ttf"],
              defaultFonts: {
                sans: ["Source Han Sans SC"],
                serif: ["Source Han Sans SC"],
                monospace: ["Source Han Sans SC"],
                cursive: ["Source Han Sans SC"],
                fantasy: ["Source Han Sans SC"]
              },
              allowScriptAccess: true,
              allowNetworking: "all",
              base: baseUrl, // Set base URL for resolving relative paths
            };
            
            // Add click handler to ensure focus
            container.addEventListener('click', forceFocus);
            
            // Add timestamp to prevent caching issues
            const timestamp = Date.now();
            swfUrl = `${swfUrl}${swfUrl.includes('?') ? '&' : '?'}t=${timestamp}`;
            
            // Load the SWF
            await player.load({
              url: swfUrl,
              allowScriptAccess: true // if false swf cant interact with page
            });
            
            // Force resize after a short delay to ensure proper sizing
            setTimeout(() => {
              if (player && player.resize) {
                const containerRect = container.getBoundingClientRect();
                player.resize(containerRect.width, containerRect.height);
              }
              
              // Ensure the canvas inside the player has the correct size
              const canvas = container.querySelector('canvas');
              if (canvas) {
                canvas.style.width = '100%';
                canvas.style.height = '100%';
              }
              
              // Force focus on the player initially
              forceFocus();
            }, 100);
          }
        }
      } catch (error) {
        console.error('Error loading Ruffle:', error);
        throw new Error('Failed to load Flash player. Please try again.');
      } finally {
        isLoadingRef.current = false;
      }
    };

    loadRuffle();

    // Cleanup function
    return () => {
      // Don't remove the Ruffle script as it should be shared across instances
      // Only clean up the player instance
      if (rufflePlayerRef.current && typeof rufflePlayerRef.current.destroy === 'function') {
        try {
          rufflePlayerRef.current.destroy();
        } catch (e) {
          console.warn('Error destroying Ruffle player:', e);
        }
      }
      
      if (currentRuffleRef) {
        currentRuffleRef.removeEventListener('click', forceFocus);
        currentRuffleRef.innerHTML = '';
      }
      
      rufflePlayerRef.current = null;
      isLoadingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.rom, game.id, game.launchoptions]); // onPlayerRef is intentionally excluded

  // Handle clicking on the container to focus the player
  const handleContainerClick = () => {
    forceFocus();
  };

  return (
    <div className="ruffle-container" onClick={handleContainerClick}>
      <div ref={ruffleRef} className="ruffle-player" />
    </div>
  );
};

Ruffle.propTypes = {
  game: PropTypes.object.isRequired,
  onPlayerRef: PropTypes.func
};

export default Ruffle;
